// ./src/hooks/useAppEssentials.ts
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import { ApiError } from "@/utils/apiResultHandler";
import { AppCache } from "@/utils/appCache";

import {
  AppEssentials,
  AppEssentialsService,
  CarBodyType,
  CarEngineType,
  CarTransmissionType,
  VehicleMake,
  ExpenseCategory,
  ExpenseKind,
  RevenueCategory,
  RevenueKind,
  GloveboxDocType,
} from "@/utils/api";

import { useApp } from "@/contexts/AppContext";

// ============================================
// Types
// ============================================

export interface UseAppEssentialsOptions {
  /** Auto-load data on mount (default: true) */
  autoLoad?: boolean;
}

export interface UseAppEssentialsReturn {
  // Data state
  appEssentials: AppEssentials | null;
  carBodyTypes: CarBodyType[];
  carEngineTypes: CarEngineType[];
  carTransmissionTypes: CarTransmissionType[];
  vehicleMakes: VehicleMake[];
  expenseCategories: ExpenseCategory[];
  expenseKinds: ExpenseKind[];
  revenueCategories: RevenueCategory[];
  revenueKinds: RevenueKind[];
  gloveboxDocTypes: GloveboxDocType[];
  isLoading: boolean;
  isLoaded: boolean;

  // Core operations
  get: () => Promise<OpResult>;
  refresh: () => Promise<OpResult>;
  forceRefresh: () => Promise<OpResult>;
  reset: () => void;

  // Lookup helpers — Car Body Types
  getCarBodyTypeById: (id: string | number | null | undefined) => CarBodyType | undefined;
  getBodyTypeNameById: (id: string | number | null | undefined) => string;

  // Lookup helpers — Engine Types
  getCarEngineTypeById: (id: string | number | null | undefined) => CarEngineType | undefined;
  getEngineTypeNameById: (id: string | number | null | undefined) => string;
  getEngineTypeByCode: (code: string | undefined) => CarEngineType | undefined;

  // Lookup helpers — Transmission Types
  getCarTransmissionTypeById: (id: string | number | null | undefined) => CarTransmissionType | undefined;
  getTransmissionTypeNameById: (id: string | number | null | undefined) => string;
  getTransmissionTypeByCode: (code: string | undefined) => CarTransmissionType | undefined;

  // Lookup helpers — Vehicle Makes
  getVehicleMakeById: (id: string | number | null | undefined) => VehicleMake | undefined;
  getVehicleMakeNameById: (id: string | number | null | undefined) => string;

  // Lookup helpers — Expense Categories
  getExpenseCategoryById: (id: string | number | null | undefined) => ExpenseCategory | undefined;
  getExpenseCategoryNameById: (id: string | number | null | undefined) => string;
  getExpenseCategoryByCode: (code: string | undefined) => ExpenseCategory | undefined;
  getExpenseCategoryByKindId: (kindId: string | number | null | undefined) => ExpenseCategory | undefined;

  // Lookup helpers — Expense Kinds
  getExpenseKindById: (id: string | number | null | undefined) => ExpenseKind | undefined;
  getExpenseKindNameById: (id: string | number | null | undefined) => string;
  getExpenseKindByCode: (code: string | undefined) => ExpenseKind | undefined;

  // Lookup helpers — Revenue Categories
  getRevenueCategoryById: (id: string | number | null | undefined) => RevenueCategory | undefined;
  getRevenueCategoryNameById: (id: string | number | null | undefined) => string;
  getRevenueCategoryByCode: (code: string | undefined) => RevenueCategory | undefined;
  getRevenueCategoryByKindId: (kindId: string | number | null | undefined) => RevenueCategory | undefined;

  // Lookup helpers — Revenue Kinds
  getRevenueKindById: (id: string | number | null | undefined) => RevenueKind | undefined;
  getRevenueKindNameById: (id: string | number | null | undefined) => string;
  getRevenueKindByCode: (code: string | undefined) => RevenueKind | undefined;

  // Lookup helpers — Glovebox Document Types
  getGloveboxDocTypeById: (id: string | number | null | undefined) => GloveboxDocType | undefined;
  getGloveboxDocTypeNameById: (id: string | number | null | undefined) => string;
  getGloveboxDocTypeByCode: (code: string | undefined) => GloveboxDocType | undefined;

  // Derived subsets
  maintenanceKinds: ExpenseKind[];
  schedulableKinds: ExpenseKind[];

  // Error handling
  lastError: ApiError | null;
  clearError: () => void;
}

// ============================================
// Constants
// ============================================

const CACHE_KEY = "essentials";
const DEFAULT_LOCALE = "en";

// ============================================
// Helpers
// ============================================

/** Build a Map<string, T> from an array, keyed by stringified `id`. */
function buildIdMap<T extends { id?: string | number }>(items: T[]): Map<string, T> {
  const map = new Map<string, T>();
  for (const item of items) {
    if (item.id !== undefined) {
      map.set(String(item.id), item);
    }
  }
  return map;
}

/** Build a Map<string, T> from an array, keyed by `code`. */
function buildCodeMap<T extends { code?: string }>(items: T[]): Map<string, T> {
  const map = new Map<string, T>();
  for (const item of items) {
    if (item.code) {
      map.set(item.code, item);
    }
  }
  return map;
}

/** Create a lookup-by-ID function backed by a Map. */
function idLookup<T>(map: Map<string, T>) {
  return (id: string | number | null | undefined): T | undefined => {
    if (id === undefined || id === null) return undefined;
    return map.get(String(id));
  };
}

/** Create a lookup-by-code function backed by a Map. */
function codeLookup<T>(map: Map<string, T>) {
  return (code: string | undefined): T | undefined => {
    if (!code) return undefined;
    return map.get(code);
  };
}

/** Create a name-getter backed by an ID lookup function. */
function nameGetter<T>(
  getById: (id: string | number | null | undefined) => T | undefined,
  nameField: string = "name"
) {
  return (id: string | number | null | undefined): string => {
    const item = getById(id);
    return item ? String((item as Record<string, unknown>)[nameField] || "") : "";
  };
}

// ============================================
// Hook Implementation
// ============================================

export const useAppEssentials = (
  options: UseAppEssentialsOptions = {}
): UseAppEssentialsReturn => {
  const { autoLoad = true } = options;

  const { user } = useApp();
  const userId = user?.id ?? null;
  const locale = user?.uiLanguage || DEFAULT_LOCALE;

  const previousUserIdRef = useRef<string | null | undefined>(undefined);
  const previousLocaleRef = useRef<string | undefined>(undefined);

  // ==========================================
  // Single source of truth
  // ==========================================

  const [appEssentials, setAppEssentials] = useState<AppEssentials | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [lastError, setLastError] = useState<ApiError | null>(null);

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // ==========================================
  // Derived arrays from appEssentials
  // ==========================================

  const carBodyTypes = useMemo(() => appEssentials?.carBodyTypes || [], [appEssentials]);
  const carEngineTypes = useMemo(() => appEssentials?.carEngineTypes || [], [appEssentials]);
  const carTransmissionTypes = useMemo(() => appEssentials?.carTransmissionTypes || [], [appEssentials]);
  const vehicleMakes = useMemo(() => appEssentials?.vehicleMakes || [], [appEssentials]);
  const expenseCategories = useMemo(() => appEssentials?.expenseCategories || [], [appEssentials]);
  const revenueCategories = useMemo(() => appEssentials?.revenueCategories || [], [appEssentials]);
  const gloveboxDocTypes = useMemo(() => appEssentials?.gloveboxDocTypes || [], [appEssentials]);

  const expenseKinds = useMemo(
    () => expenseCategories.flatMap((c) => c.kinds || []),
    [expenseCategories]
  );

  const revenueKinds = useMemo(
    () => revenueCategories.flatMap((c) => c.kinds || []),
    [revenueCategories]
  );

  // ==========================================
  // Derived subsets
  // ==========================================

  const maintenanceKinds = useMemo(
    () => expenseKinds.filter((kind) => kind.isItMaintenance),
    [expenseKinds]
  );

  const schedulableKinds = useMemo(
    () => expenseKinds.filter((kind) => kind.canSchedule),
    [expenseKinds]
  );

  // ==========================================
  // Lookup maps — by ID
  // ==========================================

  const carBodyTypesMap = useMemo(() => buildIdMap(carBodyTypes), [carBodyTypes]);
  const carEngineTypesMap = useMemo(() => buildIdMap(carEngineTypes), [carEngineTypes]);
  const carTransmissionTypesMap = useMemo(() => buildIdMap(carTransmissionTypes), [carTransmissionTypes]);
  const vehicleMakesMap = useMemo(() => buildIdMap(vehicleMakes), [vehicleMakes]);
  const expenseCategoriesMap = useMemo(() => buildIdMap(expenseCategories), [expenseCategories]);
  const expenseKindsMap = useMemo(() => buildIdMap(expenseKinds), [expenseKinds]);
  const revenueCategoriesMap = useMemo(() => buildIdMap(revenueCategories), [revenueCategories]);
  const revenueKindsMap = useMemo(() => buildIdMap(revenueKinds), [revenueKinds]);
  const gloveboxDocTypesMap = useMemo(() => buildIdMap(gloveboxDocTypes), [gloveboxDocTypes]);

  // ==========================================
  // Lookup maps — by code
  // ==========================================

  const carEngineTypesByCodeMap = useMemo(() => buildCodeMap(carEngineTypes), [carEngineTypes]);
  const carTransmissionTypesByCodeMap = useMemo(() => buildCodeMap(carTransmissionTypes), [carTransmissionTypes]);
  const expenseCategoriesByCodeMap = useMemo(() => buildCodeMap(expenseCategories), [expenseCategories]);
  const expenseKindsByCodeMap = useMemo(() => buildCodeMap(expenseKinds), [expenseKinds]);
  const revenueCategoriesByCodeMap = useMemo(() => buildCodeMap(revenueCategories), [revenueCategories]);
  const revenueKindsByCodeMap = useMemo(() => buildCodeMap(revenueKinds), [revenueKinds]);
  const gloveboxDocTypesByCodeMap = useMemo(() => buildCodeMap(gloveboxDocTypes), [gloveboxDocTypes]);

  // ==========================================
  // Lookup maps — kind ID → parent category
  // ==========================================

  const expenseKindIdToCategoryMap = useMemo(() => {
    const map = new Map<string, ExpenseCategory>();
    for (const category of expenseCategories) {
      for (const kind of category.kinds || []) {
        if (kind.id !== undefined) {
          map.set(String(kind.id), category);
        }
      }
    }
    return map;
  }, [expenseCategories]);

  const revenueKindIdToCategoryMap = useMemo(() => {
    const map = new Map<string, RevenueCategory>();
    for (const category of revenueCategories) {
      for (const kind of category.kinds || []) {
        if (kind.id !== undefined) {
          map.set(String(kind.id), category);
        }
      }
    }
    return map;
  }, [revenueCategories]);

  // ==========================================
  // Lookup functions — by ID
  // ==========================================

  const getCarBodyTypeById = useCallback(idLookup(carBodyTypesMap), [carBodyTypesMap]);
  const getCarEngineTypeById = useCallback(idLookup(carEngineTypesMap), [carEngineTypesMap]);
  const getCarTransmissionTypeById = useCallback(idLookup(carTransmissionTypesMap), [carTransmissionTypesMap]);
  const getVehicleMakeById = useCallback(idLookup(vehicleMakesMap), [vehicleMakesMap]);
  const getExpenseCategoryById = useCallback(idLookup(expenseCategoriesMap), [expenseCategoriesMap]);
  const getExpenseKindById = useCallback(idLookup(expenseKindsMap), [expenseKindsMap]);
  const getRevenueCategoryById = useCallback(idLookup(revenueCategoriesMap), [revenueCategoriesMap]);
  const getRevenueKindById = useCallback(idLookup(revenueKindsMap), [revenueKindsMap]);
  const getGloveboxDocTypeById = useCallback(idLookup(gloveboxDocTypesMap), [gloveboxDocTypesMap]);

  // ==========================================
  // Lookup functions — by code
  // ==========================================

  const getEngineTypeByCode = useCallback(codeLookup(carEngineTypesByCodeMap), [carEngineTypesByCodeMap]);
  const getTransmissionTypeByCode = useCallback(codeLookup(carTransmissionTypesByCodeMap), [carTransmissionTypesByCodeMap]);
  const getExpenseCategoryByCode = useCallback(codeLookup(expenseCategoriesByCodeMap), [expenseCategoriesByCodeMap]);
  const getExpenseKindByCode = useCallback(codeLookup(expenseKindsByCodeMap), [expenseKindsByCodeMap]);
  const getRevenueCategoryByCode = useCallback(codeLookup(revenueCategoriesByCodeMap), [revenueCategoriesByCodeMap]);
  const getRevenueKindByCode = useCallback(codeLookup(revenueKindsByCodeMap), [revenueKindsByCodeMap]);
  const getGloveboxDocTypeByCode = useCallback(codeLookup(gloveboxDocTypesByCodeMap), [gloveboxDocTypesByCodeMap]);

  // ==========================================
  // Lookup functions — kind ID → parent category
  // ==========================================

  const getExpenseCategoryByKindId = useCallback(idLookup(expenseKindIdToCategoryMap), [expenseKindIdToCategoryMap]);
  const getRevenueCategoryByKindId = useCallback(idLookup(revenueKindIdToCategoryMap), [revenueKindIdToCategoryMap]);

  // ==========================================
  // Lookup functions — name getters
  // ==========================================

  const getBodyTypeNameById = useCallback(nameGetter(getCarBodyTypeById), [getCarBodyTypeById]);
  const getEngineTypeNameById = useCallback(nameGetter(getCarEngineTypeById), [getCarEngineTypeById]);
  const getTransmissionTypeNameById = useCallback(nameGetter(getCarTransmissionTypeById), [getCarTransmissionTypeById]);
  const getVehicleMakeNameById = useCallback(nameGetter(getVehicleMakeById, "makeName"), [getVehicleMakeById]);
  const getExpenseCategoryNameById = useCallback(nameGetter(getExpenseCategoryById), [getExpenseCategoryById]);
  const getExpenseKindNameById = useCallback(nameGetter(getExpenseKindById), [getExpenseKindById]);
  const getRevenueCategoryNameById = useCallback(nameGetter(getRevenueCategoryById), [getRevenueCategoryById]);
  const getRevenueKindNameById = useCallback(nameGetter(getRevenueKindById), [getRevenueKindById]);
  const getGloveboxDocTypeNameById = useCallback(nameGetter(getGloveboxDocTypeById), [getGloveboxDocTypeById]);

  // ==========================================
  // Reset
  // ==========================================

  const reset = useCallback(() => {
    setAppEssentials(null);
    setLastError(null);
    setIsLoading(false);
    setIsLoaded(false);
    previousUserIdRef.current = undefined;
    previousLocaleRef.current = undefined;
  }, []);

  // ==========================================
  // Cache helpers
  // ==========================================

  /**
   * Read from the locale-aware cache.
   * Returns null on miss, expiry, version mismatch, or locale mismatch.
   */
  const readCache = useCallback((forLocale: string): AppEssentials | null => {
    const result = AppCache.get<AppEssentials>(CACHE_KEY, { locale: forLocale });
    return result?.data ?? null;
  }, []);

  /**
   * Write to the locale-aware cache.
   * Essentials are shared (userId: null) — not tied to any user.
   */
  const writeCache = useCallback((data: AppEssentials, forLocale: string): void => {
    AppCache.set(CACHE_KEY, data, { locale: forLocale });
  }, []);

  // ==========================================
  // Core operations
  // ==========================================

  /**
   * Load essentials: try cache first, fetch from API on miss.
   * On successful fetch, updates the cache.
   */
  const get = useCallback(async (): Promise<OpResult> => {
    // Use the current locale from the ref (always up-to-date inside callbacks)
    const currentLocale = previousLocaleRef.current || DEFAULT_LOCALE;

    // 1. Try cache
    const cached = readCache(currentLocale);
    if (cached) {
      setAppEssentials(cached);
      setIsLoaded(true);
      setLastError(null);
      return OpResult.ok(cached);
    }

    // 2. Cache miss — fetch from API
    setIsLoading(true);
    setLastError(null);

    try {
      const result = await AppEssentialsService.get();

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = Array.isArray(result.data)
          ? (result.data as AppEssentials[])[0]
          : (result.data as AppEssentials);

        setAppEssentials(data);
        setIsLoaded(true);
        setLastError(null);

        // Write to cache for next load
        writeCache(data, currentLocale);

        return result;
      }

      setLastError(ApiError.fromResult(result, "Failed to load application data"));
      setAppEssentials(null);
      setIsLoaded(true);
      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(ApiError.fromException(error, "Failed to load app essentials"));
      setAppEssentials(null);
      setIsLoaded(true);
      return result;
    } finally {
      setIsLoading(false);
    }
  }, [readCache, writeCache]);

  /**
   * Force refresh: bypass cache, always fetch from API, update cache.
   */
  const forceRefresh = useCallback(async (): Promise<OpResult> => {
    const currentLocale = previousLocaleRef.current || DEFAULT_LOCALE;

    // Clear the cache entry so next `get` won't use stale data if this fetch fails
    AppCache.remove(CACHE_KEY);

    setIsLoading(true);
    setLastError(null);

    try {
      const result = await AppEssentialsService.get();

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = Array.isArray(result.data)
          ? (result.data as AppEssentials[])[0]
          : (result.data as AppEssentials);

        setAppEssentials(data);
        setIsLoaded(true);
        setLastError(null);

        writeCache(data, currentLocale);

        return result;
      }

      setLastError(ApiError.fromResult(result, "Failed to load application data"));
      setAppEssentials(null);
      setIsLoaded(true);
      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(ApiError.fromException(error, "Failed to load app essentials"));
      setAppEssentials(null);
      setIsLoaded(true);
      return result;
    } finally {
      setIsLoading(false);
    }
  }, [writeCache]);

  // Stable refs for use in effects
  const getRef = useRef(get);
  getRef.current = get;
  const forceRefreshRef = useRef(forceRefresh);
  forceRefreshRef.current = forceRefresh;

  // ==========================================
  // Effects
  // ==========================================

  /**
   * Main lifecycle effect — handles:
   * - Initial load (cache-first)
   * - User sign-out (reset state, keep cache)
   * - User sign-in (load if not already loaded)
   * - Language change (force refresh with new locale)
   */
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    const previousLocale = previousLocaleRef.current;

    previousUserIdRef.current = userId;
    previousLocaleRef.current = locale;

    // --- Initial mount ---
    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        getRef.current();
      }
      return;
    }

    // --- Language changed (regardless of user change) ---
    // This takes priority: new language means the cached data has wrong translations.
    // AppCache.get will return null due to locale mismatch, so forceRefresh
    // ensures we fetch fresh data and cache it with the new locale.
    if (previousLocale !== undefined && previousLocale !== locale && userId) {
      forceRefreshRef.current();
      return;
    }

    // --- User changed ---
    if (previousUserId !== userId) {
      if (!userId) {
        // Signed out — reset hook state but DON'T clear essentials cache
        // (essentials are shared, next user will benefit from cached data
        // as long as locale and app version match)
        setAppEssentials(null);
        setLastError(null);
        setIsLoading(false);
        setIsLoaded(false);
      } else if (autoLoad) {
        // Signed in (or switched user) — load (will use cache if locale matches)
        getRef.current();
      }
    }
  }, [userId, locale, autoLoad]);

  // ==========================================
  // Return
  // ==========================================

  return useMemo(
    () => ({
      // Data state
      appEssentials,
      carBodyTypes,
      carEngineTypes,
      carTransmissionTypes,
      vehicleMakes,
      expenseCategories,
      expenseKinds,
      revenueCategories,
      revenueKinds,
      gloveboxDocTypes,
      isLoading,
      isLoaded,

      // Core operations
      get,
      refresh: get,
      forceRefresh,
      reset,

      // Lookups — by ID
      getCarBodyTypeById,
      getCarEngineTypeById,
      getCarTransmissionTypeById,
      getVehicleMakeById,
      getExpenseCategoryById,
      getExpenseKindById,
      getRevenueCategoryById,
      getRevenueKindById,
      getGloveboxDocTypeById,

      // Lookups — by code
      getEngineTypeByCode,
      getTransmissionTypeByCode,
      getExpenseCategoryByCode,
      getExpenseKindByCode,
      getRevenueCategoryByCode,
      getRevenueKindByCode,
      getGloveboxDocTypeByCode,

      // Lookups — kind ID → parent category
      getExpenseCategoryByKindId,
      getRevenueCategoryByKindId,

      // Lookups — name getters
      getBodyTypeNameById,
      getEngineTypeNameById,
      getTransmissionTypeNameById,
      getVehicleMakeNameById,
      getExpenseCategoryNameById,
      getExpenseKindNameById,
      getRevenueCategoryNameById,
      getRevenueKindNameById,
      getGloveboxDocTypeNameById,

      // Derived subsets
      maintenanceKinds,
      schedulableKinds,

      // Error handling
      lastError,
      clearError,
    }),
    [
      appEssentials,
      carBodyTypes,
      carEngineTypes,
      carTransmissionTypes,
      vehicleMakes,
      expenseCategories,
      expenseKinds,
      revenueCategories,
      revenueKinds,
      gloveboxDocTypes,
      isLoading,
      isLoaded,
      get,
      forceRefresh,
      reset,
      getCarBodyTypeById,
      getCarEngineTypeById,
      getCarTransmissionTypeById,
      getVehicleMakeById,
      getExpenseCategoryById,
      getExpenseKindById,
      getRevenueCategoryById,
      getRevenueKindById,
      getGloveboxDocTypeById,
      getEngineTypeByCode,
      getTransmissionTypeByCode,
      getExpenseCategoryByCode,
      getExpenseKindByCode,
      getRevenueCategoryByCode,
      getRevenueKindByCode,
      getGloveboxDocTypeByCode,
      getExpenseCategoryByKindId,
      getRevenueCategoryByKindId,
      getBodyTypeNameById,
      getEngineTypeNameById,
      getTransmissionTypeNameById,
      getVehicleMakeNameById,
      getExpenseCategoryNameById,
      getExpenseKindNameById,
      getRevenueCategoryNameById,
      getRevenueKindNameById,
      getGloveboxDocTypeNameById,
      maintenanceKinds,
      schedulableKinds,
      lastError,
      clearError,
    ]
  );
};